import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  txCardQuery,
  getTxState,
  isRenterPendingRequest,
  isRenterUpcoming,
  isRenterPast,
} from '../../util/transactionCards';

// ================ Action types ================ //

const LOAD_DATA_REQUEST = 'app/RenterDashboardPage/LOAD_DATA_REQUEST';
const LOAD_DATA_SUCCESS = 'app/RenterDashboardPage/LOAD_DATA_SUCCESS';
const LOAD_DATA_ERROR = 'app/RenterDashboardPage/LOAD_DATA_ERROR';

// ================ Reducer ================ //

const initialState = {
  upcomingTransactions: [],   // Active orders as buyer (host has accepted)
  pendingTransactions: [],    // Orders awaiting host accept (preauthorized)
  pastTransactions: [],       // Completed/cancelled orders as buyer
  transactionsLoaded: false,
  transactionsError: null,
  activeFilter: 'upcoming',  // 'upcoming' | 'past' | 'all'
  stats: {
    totalRentals: 0,
    totalSpent: 0,
    averageRatingGiven: 0,
  },
};

export default function renterDashboardPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  
  switch (type) {
    case LOAD_DATA_REQUEST:
      return {
        ...state,
        transactionsLoaded: false,
        transactionsError: null,
      };
    
    case LOAD_DATA_SUCCESS:
      const { upcomingTransactions, pendingTransactions, pastTransactions, stats } = payload;
      return {
        ...state,
        upcomingTransactions,
        pendingTransactions,
        pastTransactions,
        stats,
        transactionsLoaded: true,
        transactionsError: null,
      };
    
    case LOAD_DATA_ERROR:
      return {
        ...state,
        transactionsLoaded: true,
        transactionsError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const loadDataRequest = () => ({
  type: LOAD_DATA_REQUEST,
});

const loadDataSuccess = (upcomingTransactions, pendingTransactions, pastTransactions, stats) => ({
  type: LOAD_DATA_SUCCESS,
  payload: { upcomingTransactions, pendingTransactions, pastTransactions, stats },
});

const loadDataError = error => ({
  type: LOAD_DATA_ERROR,
  payload: error,
});

// ================ Helper functions ================ //

// Bucket which refs to store. Classification is the process-aware shared
// SSOT (isRenterPendingRequest / isRenterUpcoming / isRenterPast in
// util/transactionCards) so the duck's stored refs and the component's
// render filter can't drift — previously these were two separate, and
// subtly different, state lists. Pending + Upcoming + Past now span BOTH
// the booking and the custom-quote (negotiation) processes; unclassified
// states (e.g. negotiation `inquiry`) are intentionally skipped.
const categorizeTransactions = (transactions) => {
  const upcoming = [];
  const pending = [];
  const past = [];

  transactions.forEach(transaction => {
    const ref = { id: transaction.id, type: 'transaction' };

    if (isRenterPendingRequest(transaction)) {
      pending.push(ref);
    } else if (isRenterUpcoming(transaction)) {
      upcoming.push(ref);
    } else if (isRenterPast(transaction)) {
      past.push(ref);
    }
    // Unclassified states are skipped (and counted as unresolved upstream).
  });

  return { upcoming, pending, past };
};

const calculateStatsFromTransactions = (transactions) => {
  const completedTransactions = transactions.filter(
    tx => getTxState(tx) === 'completed' || getTxState(tx) === 'reviewed'
  );
  
  const totalSpent = completedTransactions.reduce((sum, tx) => {
    const total = tx?.attributes?.payinTotal;
    return sum + (total || 0);
  }, 0);

  // Calculate average rating given (from reviews the renter has left)
  // Note: reviews loaded via `include` are attached to `tx.reviews`, not via the raw
  // relationships.data — but we keep this check compatible with either shape.
  const reviewsGiven = transactions.filter(tx => {
    const reviews = tx?.reviews || tx?.relationships?.reviews?.data || [];
    return reviews.some(review => review?.attributes?.type === 'ofProvider');
  });
  
  const totalRating = reviewsGiven.reduce((sum, tx) => {
    const reviews = tx?.reviews || tx?.relationships?.reviews?.data || [];
    const providerReview = reviews.find(r => r?.attributes?.type === 'ofProvider');
    return sum + (providerReview?.attributes?.rating || 0);
  }, 0);
  
  const averageRatingGiven = reviewsGiven.length > 0 ? totalRating / reviewsGiven.length : 0;

  return {
    totalRentals: completedTransactions.length,
    totalSpent,
    averageRatingGiven
  };
};

// ================ Thunks ================ //

export const loadData = (params, search) => {
  return (dispatch, getState, sdk) => {
    dispatch(loadDataRequest());
    
    // Shared card data contract — see util/transactionCards.js.
    return sdk.transactions.query(txCardQuery({ only: 'order' }))
    .then(response => {
      // Add entities to marketplace data store
      dispatch(addMarketplaceEntities(response));
      
      const transactions = response.data.data || [];
      
      // Categorize transactions into pending, upcoming, and past
      const { upcoming, pending, past } = categorizeTransactions(transactions);

      // Calculate stats
      const stats = calculateStatsFromTransactions(transactions);

      dispatch(loadDataSuccess(upcoming, pending, past, stats));
      
      return response;
    })
    .catch(error => {
      console.error('Error loading renter dashboard data:', error);
      dispatch(loadDataError(error));
      throw error;
    });
  };
};